<template>
    <div class="modal-container">
        <div>
            <div class="modal-header">
                <h4 class="modal-title">Update Order</h4>
                <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                    <md-icon>clear</md-icon>
                </md-button>
            </div>
            <div class="modal-body">
                <form-wrapper v-if="!isLoading" :validator="$v.orderDetails" class="form-wrapper">
                    <form-group name="name" label="Shipping Contact Name">
                        <md-input v-model="orderDetails.contact.name" />
                    </form-group>
                    <form-group name="phone" label="Shipping Contact Phone">
                        <md-input v-model="orderDetails.contact.phone" />
                    </form-group>
                    <form-group name="email" label="Shipping Contact Email">
                        <md-input v-model="orderDetails.contact.email" />
                    </form-group>
                    <div>
                        <google-autocomplete
                            label="Shipping Address"
                            id="autoaddress"
                            classname="form-control autocomplete-input"
                            placeholder=""
                            :should-focus="false"
                            v-model="tempAddressData.shippingAddress"
                            name="shippingAddress.address"
                        ></google-autocomplete>
                    </div>
                    <div>
                        <md-datepicker
                            ref="mdDatePicker"
                            v-model="orderDetails.expectedDeliveryDate"
                            md-immediately
                            :md-debounce="10"
                        >
                            <md-tooltip md-direction="left">
                                The delivery date refers to the scheduled date for a stop once the delivery is
                                confirmed.
                            </md-tooltip>
                            <label>Delivery Date</label>
                        </md-datepicker>
                        <div v-if="isDropStopAssigned" class="md-helper-text-datepicker">
                            This delivery is already assigned to a team member. Updating the expected delivery date will impact the team member's trip.
                        </div>
                    </div>
                    <div>
                        <md-datepicker
                            ref="mdDatePicker"
                            v-model="orderDetails.readyPickupAvailableDate"
                            md-immediately
                            :md-debounce="10"
                        >
                            <md-tooltip md-direction="left">
                                The ready pickup date refers to the scheduled date for a stop once the package is ready
                                to be picked up.
                            </md-tooltip>
                            <label>Ready Pickup Date</label>
                        </md-datepicker>
                    </div>
                    <div>
                        <md-datepicker
                            ref="mdDatePicker"
                            v-model="orderDetails.readyDeliveryAvailableDate"
                            md-immediately
                            :md-debounce="10"
                        >
                            <md-tooltip md-direction="left">
                                The ready delivery date is the earliest available date the recipient can schedule this
                                order for delivery.
                            </md-tooltip>
                            <label>Ready Delivery Date</label>
                        </md-datepicker>
                    </div>
                </form-wrapper>
                <div v-else>
                    <div class="loader-container">
                        <fade-loader :loading="true" color="#333333" />
                        <span>LOADING</span>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <md-button v-if="!isLoading" class="dialog-button md-primary" @click.stop="updateOrder">
                    Update
                </md-button>
                <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
            </div>
        </div>
    </div>
</template>

<script>
import { GoogleAutocomplete } from '@/components';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { handleRequests, showErrorMessage } from '@/helpers';
import { DATE_TYPES } from '@/utils/constants';
import { GeneralMixin } from '@/mixins/GeneralMixin';

export default {
    name: 'UpdateOrderModal',
    mixins: [GeneralMixin],
    components: {
        FadeLoader,
        GoogleAutocomplete
    },
    props: {
        orderId: {
            type: Number,
            default: null
        },
        isDropStopAssigned: {
            type: Boolean,
            default: false
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            orderDetails: {
                contact: {
                    name: null,
                    phone: null,
                    email: null
                },
                expectedDeliveryDate: null,
                readyPickupAvailableDate: null,
                readyDeliveryAvailableDate: null,
                shippingAddress: {
                    name: null,
                    address: null,
                    location: {
                        latitude: null,
                        longitude: null
                    }
                }
            },
            isFormTouched: false,
            isLoading: true,
            tempAddressData: {
                shippingAddress: {
                    name: null,
                    address: null,
                    location: {
                        latitude: null,
                        longitude: null
                    },
                    coordinateAddress: ''
                }
            }
        };
    },
    async mounted() {
        await this.getOrderDetails(this.orderId);
    },
    methods: {
        /**
         *
         * @param {import("@/jsDocTypes/RateRule").RateRule} rateRule
         */
        handleRateTypeChanged(rateRule) {
            this.rateGroupId = rateRule?.rateGroupId ?? null;
        },
        handleCustomerChanged(customerId) {
            this.customerId = !customerId ? 0 : customerId;
        },
        async getOrderDetails(orderId) {
            const api = `/api/orders/${orderId}`;

            try {
                const data = await handleRequests(api);
                this.isLoading = false;

                const {
                    contact,
                    shippingAddress,
                    expectedDeliveryDate,
                    readyDeliveryAvailableDate,
                    readyPickupAvailableDate
                } = data.data;

                this.orderDetails = {
                    contact: {
                        name: contact?.name ?? null,
                        phone: contact?.phone ?? null,
                        email: contact?.email ?? null
                    },
                    shippingAddress,
                    expectedDeliveryDate: expectedDeliveryDate ? this.formatDate(expectedDeliveryDate) : null,
                    readyPickupAvailableDate: readyPickupAvailableDate
                        ? this.formatDate(readyPickupAvailableDate)
                        : null,
                    readyDeliveryAvailableDate: readyDeliveryAvailableDate
                        ? this.formatDate(readyDeliveryAvailableDate)
                        : null
                };

                this.tempAddressData.shippingAddress = shippingAddress;
            } catch (e) {
                const message = 'Could not retrieve order details. Please try again later.';
                showErrorMessage(this, message, e);
                this.resolve(false);
            }
        },
        formatDate(date) {
            return this.$options.filters.timeFormat(date, DATE_TYPES.internationalDate);
        },
        async updateOrder() {
            this.orderDetails.shippingAddress = this.tempAddressData.shippingAddress;
            if (this.tempAddressData.shippingAddress?.addressComponents) {
                this.orderDetails.shippingAddress = {
                    ...this.orderDetails.shippingAddress,
                    ...this.tempAddressData.shippingAddress.addressComponents
                };
            }

            const data = {
                ...this.orderDetails,
                expectedDeliveryDate: this.orderDetails.expectedDeliveryDate
                    ? this.formatDate(this.orderDetails.expectedDeliveryDate)
                    : null,
                readyPickupAvailableDate: this.orderDetails.readyPickupAvailableDate
                    ? this.formatDate(this.orderDetails.readyPickupAvailableDate)
                    : null,
                readyDeliveryAvailableDate: this.orderDetails.readyDeliveryAvailableDate
                    ? this.formatDate(this.orderDetails.readyDeliveryAvailableDate)
                    : null
            };

            this.$_handleLoaderState(true, 'UPDATING...');
            const api = `/api/orders/${this.orderId}`;
            const payload = {
                method: 'put',
                data
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated the trip.',
                    type: 'success'
                });
                this.$v.$reset();
                this.resolve({ code: 'ok', data: this.orderDetails });
            } catch (e) {
                const message = 'Could not update order.';
                showErrorMessage(this, message, e);
                this.resolve('error');
            }
            this.$_handleLoaderState(false);
        }
    },
    validations: {
        orderDetails: {}
    }
};
</script>

<style lang="scss" scoped>
.modal-body {
    min-height: 200px;
}

.md-helper-text-datepicker {
    color: #ff5245;
    margin-left: 36px;
    font-size: 12px;
    transition: .3s cubic-bezier(.4, 0, .2, 1);
}
</style>
